<template>
  <div class="broadcast">
    <div class="state" :class="stateClass">
      <StatusBadge :status="publication.status" :status-enum="PUBLICATION_STATUS_ENUM" />
      <span class="state-text">{{ stateText }}</span>
    </div>

    <p v-if="remainingChecks.length > 0" class="alert">
      <span class="fas fa-triangle-exclamation"></span>
      {{ remainingChecks.length }} point{{ remainingChecks.length > 1 ? 's' : '' }} à vérifier non coché{{ remainingChecks.length > 1 ? 's' : '' }} :
      {{ remainingChecks.map(check => check.label).join(' · ') }}
    </p>

    <div class="columns">
      <div class="column">
        <h3 class="panel-title">Publier sur {{ channelLabel }}</h3>
        <p class="hint">Le module ne publie pas à votre place : copiez le texte, collez-le sur votre page {{ channelLabel }}, ajoutez l'image puis le premier commentaire.</p>
        <div class="buttons">
          <button type="button" class="panel-button" :disabled="!hasText" @click="copy('text')">Copier le texte</button>
          <button type="button" class="panel-button" :disabled="!hasComment" @click="copy('firstComment')">Copier le premier commentaire</button>
        </div>
        <p v-if="!hasText" class="hint">Le texte du post n'est pas encore écrit (onglet Rédaction).</p>
      </div>

      <div class="column">
        <template v-if="!isPublished">
          <h3 class="panel-title">Programmer</h3>
          <CustomTextField :content=inputs.scheduledAt />
          <div class="buttons">
            <button type="button" class="panel-button" :disabled="!inputs.scheduledAt.value" @click="schedule">{{ isScheduled ? 'Changer la date' : 'Programmer' }}</button>
            <button v-if="isScheduled" type="button" class="panel-button" @click="unschedule">Déprogrammer</button>
          </div>
          <p class="hint">Un repère pour vous : la publication reste à faire à la main à cette date.</p>

          <h3 class="panel-title">Déjà publié ?</h3>
          <div class="fields-row">
            <CustomTextField :content=inputs.publishedAt />
            <CustomTextField :content=inputs.externalUrl />
          </div>
          <div class="buttons">
            <button type="button" class="panel-button key" @click="publish">Marquer comme publié</button>
          </div>
          <p class="hint">La date et le lien sont facultatifs : sans date, la publication s'affiche « Date non définie ».</p>
        </template>

        <template v-else>
          <h3 class="panel-title">Publication</h3>
          <div class="fields-row">
            <CustomTextField :content=inputs.publishedAt />
            <CustomTextField :content=inputs.externalUrl />
          </div>
          <div class="buttons">
            <a v-if="safeUrl" class="panel-button link-button" :href="safeUrl" target="_blank" rel="noopener noreferrer">Ouvrir le post</a>
            <button type="button" class="panel-button danger" @click="unpublish">Annuler la publication</button>
          </div>
          <p class="hint">Les modifications de la date et du lien sont enregistrées avec la publication.</p>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import {CHANNEL_ENUM, PUBLICATION_STATUS_ENUM, formatDateTime, labelOf} from "@/marketing/utils";
library.add(faTriangleExclamation);

/**
 * Onglet Diffusion : l'état de la publication, la programmation, « Marquer comme publié » et les textes à copier.
 * schedule(), unschedule(), publish(), unpublish() et copyField(champ) sont portés par la modale.
 */
export default {
  name: 'PublicationBroadcast',
  props: ['publication', 'topic', 'inputs', 'schedule', 'unschedule', 'publish', 'unpublish', 'copyField'],
  components: {CustomTextField, StatusBadge},
  data() {
    return {PUBLICATION_STATUS_ENUM};
  },
  computed: {
    isPublished() {
      return this.publication.status === PUBLICATION_STATUS_ENUM.PUBLISHED.value;
    },
    isScheduled() {
      return this.publication.status === PUBLICATION_STATUS_ENUM.SCHEDULED.value;
    },
    channelLabel() {
      return labelOf(CHANNEL_ENUM, this.publication.channel, 'le réseau');
    },
    hasText() {
      return String(this.inputs.text.value ?? '').trim() !== '';
    },
    hasComment() {
      return String(this.inputs.firstComment.value ?? '').trim() !== '';
    },
    remainingChecks() {
      return (this.topic?.checks ?? []).filter(check => !check.done);
    },
    stateText() {
      if (this.isPublished) return this.publication.publishedAt ? `Publiée le ${formatDateTime(this.publication.publishedAt)}` : "Publiée, date non définie";
      if (this.isScheduled) return `Programmée le ${formatDateTime(this.publication.scheduledAt)}`;
      return "Brouillon : pas encore programmée";
    },
    stateClass() {
      return this.isPublished ? 'published' : (this.isScheduled ? 'scheduled' : '');
    },
    // Seul un lien web est ouvrable (le service refuse les autres)
    safeUrl() {
      const url = String(this.inputs.externalUrl.value ?? '').trim();
      return /^https?:\/\//i.test(url) ? url : '';
    },
  },
  methods: {
    copy(field) {
      return this.copyField(field);
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/marketing/style/modal.styl';

.column
  min-width 0

.state
  display flex
  align-items center
  gap 12px
  margin-bottom 10px
  padding 10px 14px
  border-radius CTRL_RADIUS
  background CTRL_BG

  &.scheduled
    background #eef4ff

  &.published
    background #eef8f0

.state-text
  font-weight 700
  color THEME_COLOR_PRIMARY

.alert
  margin 0 0 8px
  padding 8px 12px
  border-radius CTRL_RADIUS
  background #fff6e5
  color #8a4b00
  font-size 0.9em

.buttons
  display flex
  flex-wrap wrap
  gap 10px
  margin 6px 0

.link-button
  display inline-flex
  align-items center
  text-decoration none
</style>
